import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import { useBenefitQuoteMutation } from '../../../../services/servicePostAJob';
import {
  IJobBenefitOption,
  IJobBenefitSelection,
} from '../../../../services/servicePostAJob/responseInterfaces';
import { readHeldBenefitClaim } from '../../../../utils/benefitClaim';
import { TOKENS } from '../../../../styles/tokens';

interface IBenefitSelectorProps {
  siteId: string;
  listingType: number;
  couponCode?: string;
  onSelectionChange: (selection: IJobBenefitSelection | null) => void;
  onQuoteBlockedChange: (blocked: boolean) => void;
  /** Definitive rejection of the current supplied code (null when none). */
  onQuoteErrorChange?: (message: string | null) => void;
}

const optionKey = (option: IJobBenefitOption): string => [
  option.sourceType,
  option.benefitRuleId || '',
  option.couponId || '',
  option.organizationId || '',
  option.subscriptionId || '',
  option.subscriptionPaymentId || '',
].join(':');

/** Format the repository's zone-less ET wall clock without converting time zones. */
export const formatBenefitDeadline = (value: string): string => {
  const match = value && value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return `${value} ET`;
  const [, year, month, day, hourText, minute] = match;
  const hour = Number(hourText);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  const monthName = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(month) - 1];
  return `${monthName} ${Number(day)}, ${year} at ${displayHour}:${minute} ${period} ET`;
};

// Customers see "code", never "coupon" (matches PromoCodeField's scrubbing).
const responseError = (error: unknown): string => {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: { error?: string } }).data;
    if (data?.error) return data.error.replace(/coupon code/gi, 'code').replace(/coupon/gi, 'code');
  }
  return 'We could not check job credits. Please try again.';
};

const responseStatus = (error: unknown): number | undefined => (
  error && typeof error === 'object' && 'status' in error
    ? Number((error as { status?: number }).status)
    : undefined
);

export const BenefitSelector = memo(({
  siteId,
  listingType,
  couponCode,
  onSelectionChange,
  onQuoteBlockedChange,
  onQuoteErrorChange,
}: IBenefitSelectorProps) => {
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [resolvedQuoteKey, setResolvedQuoteKey] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [quote, { data, error, isLoading }] = useBenefitQuoteMutation();
  // No organization is ever asked for: the quote returns every eligible organization's
  // credits (tagged with organizationId) and the soonest-expiring one is applied.
  const quoteKey = `${siteId}:${listingType}:${couponCode || ''}`;

  useEffect(() => {
    let active = true;
    setResolvedQuoteKey(null);
    // Read at call time, not captured: a claim can be reserved (or released) after this
    // component mounts. Sending it keeps a reservation this checkout already holds — which a
    // reload leaves behind on the server — out of its own remaining-use count.
    const heldClaim = readHeldBenefitClaim();
    const request = quote({
      siteId,
      listingType,
      ...(couponCode ? { couponCode } : {}),
      ...(heldClaim ? { existingClaimCheckoutGuid: heldClaim.claimCheckoutGuid } : {}),
    });
    void request.then(() => {
      if (active) setResolvedQuoteKey(quoteKey);
    });
    return () => { active = false; };
  }, [siteId, listingType, couponCode, quote, quoteKey, retryNonce]);

  // A supplied code blocks checkout until its current quote resolves. Automatic
  // benefit discovery remains optional when no code was supplied.
  useEffect(() => {
    const quotePending = resolvedQuoteKey !== quoteKey;
    onQuoteBlockedChange(Boolean(quotePending || isLoading || (couponCode && error)));
  }, [couponCode, error, isLoading, onQuoteBlockedChange, quoteKey, resolvedQuoteKey]);

  useEffect(() => {
    const isCurrentDefinitiveRejection = Boolean(
      couponCode && error && resolvedQuoteKey === quoteKey && responseStatus(error) === 400
    );
    onQuoteErrorChange?.(isCurrentDefinitiveRejection ? responseError(error) : null);
  }, [couponCode, error, onQuoteErrorChange, quoteKey, resolvedQuoteKey]);

  const options = useMemo(() => (data?.options || []).slice().sort((a, b) => (
    (a.expiresAt || '').localeCompare(b.expiresAt || '')
  )), [data]);

  useEffect(() => {
    if (resolvedQuoteKey !== quoteKey || isLoading || error) return;
    if (!options.length) {
      setSelectedKey('');
      onSelectionChange(null);
      return;
    }
    // Coupon precedence is enforced server-side; when one is supplied its option is
    // the default. Otherwise choose the credit expiring soonest.
    const selected = options.find((option) => option.sourceType === 'coupon') || options[0];
    setSelectedKey(optionKey(selected));
    onSelectionChange({
      organizationId: selected.organizationId ?? undefined,
      option: selected,
    });
  }, [options, onSelectionChange, isLoading, error, couponCode, quoteKey, resolvedQuoteKey]);

  const chooseOption = (key: string) => {
    setSelectedKey(key);
    if (!key) {
      onSelectionChange(null);
      return;
    }
    const option = options.find((item) => optionKey(item) === key);
    onSelectionChange(option ? {
      organizationId: option.organizationId ?? undefined,
      option,
    } : null);
  };

  const quoteError = error ? responseError(error) : null;
  if (!options.length && !quoteError && !isLoading) return null;

  return (
    <Box
      data-cy="jobBenefitSelector"
      sx={{
        mt: 3,
        p: 2.5,
        border: `1px solid ${TOKENS.lineSoft}`,
        borderRadius: '8px',
        backgroundColor: '#fff',
      }}
    >
      <Typography variant="h6" fontWeight={700}>Job credits</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Eligible organization and promotional credits can be applied to this listing.
      </Typography>

      {quoteError && (
        <Alert
          severity="error"
          data-cy="benefitQuoteError"
          sx={{ mb: 2 }}
          action={responseStatus(error) !== 400 ? (
            <Button color="inherit" size="small" onClick={() => setRetryNonce((value) => value + 1)}>
              Try again
            </Button>
          ) : undefined}
        >
          {quoteError}
        </Alert>
      )}

      {options.length > 0 && (
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">Available credits</FormLabel>
          <RadioGroup value={selectedKey} onChange={(event) => chooseOption(event.target.value)}>
            {options.map((option) => (
              <FormControlLabel
                key={optionKey(option)}
                value={optionKey(option)}
                control={<Radio color="primary" />}
                label={(
                  <Box sx={{ py: 0.75 }}>
                    <Typography variant="body1" fontWeight={700}>{option.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ${option.discountAmount} credit · ${option.finalAmount} due · {option.remainingUses} remaining
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Redeem by {formatBenefitDeadline(option.expiresAt)}
                    </Typography>
                  </Box>
                )}
              />
            ))}
            {!couponCode && (
              <FormControlLabel
                value=""
                control={<Radio color="primary" />}
                label="Do not use a job credit"
              />
            )}
          </RadioGroup>
        </FormControl>
      )}
    </Box>
  );
});

export default BenefitSelector;
